import { RuntimeDefectReporter } from '@st/redux'
import { Deferred, Effect, Layer, Queue } from 'effect'
import {
  MuPDFDocumentOpenError,
  MuPDFPageRenderError,
  MuPDFPageTextExtractionError,
  MuPDFPlatform,
  MuPDFWorkerCrashedError
} from './mupdf-platform'
import {
  type MuPDFWorkerMethod,
  type MuPDFWorkerCrashedMessage,
  type MuPDFWorkerRequest,
  type MuPDFWorkerRequestByMethod,
  type MuPDFWorkerResponse,
  type MuPDFWorkerResultByMethod
} from './workers/mupdf-worker-protocol'

class MuPDFWorkerRequestError extends Error {
  readonly _tag = 'MuPDFWorkerRequestError' as const

  constructor(readonly cause: Error) {
    super(cause.message)
  }
}

class MuPDFWorkerSendError extends Error {
  readonly _tag = 'MuPDFWorkerSendError' as const

  constructor(readonly cause: Error) {
    super(cause.message)
  }
}

type MuPDFWorkerError = MuPDFWorkerRequestError | MuPDFWorkerCrashedError
type MuPDFWorkerResult = MuPDFWorkerResultByMethod<MuPDFWorkerMethod>
type PendingRequest = Deferred.Deferred<MuPDFWorkerResult, MuPDFWorkerError>

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error))
}

function isMuPDFWorkerReadyMessage(value: unknown): value is { type: 'ready' } {
  return typeof value == 'object' && value != null && (value as { type?: unknown }).type == 'ready'
}

function isMuPDFWorkerCrashedMessage(value: unknown): value is MuPDFWorkerCrashedMessage {
  if (typeof value != 'object' || value == null) {
    return false
  }

  const message = value as { type?: unknown; error?: unknown }
  return message.type == 'crashed' && typeof message.error == 'string'
}

function isMuPDFWorkerResponse(value: unknown): value is MuPDFWorkerResponse {
  if (typeof value != 'object' || value == null) {
    return false
  }

  const maybeMessage = value as { id?: unknown; result?: unknown; error?: unknown }
  return (
    typeof maybeMessage.id == 'number' &&
    ('result' in maybeMessage || typeof maybeMessage.error == 'string')
  )
}

function closeUnclaimedBitmap(value: unknown) {
  if (typeof ImageBitmap != 'undefined' && value instanceof ImageBitmap) {
    value.close()
  }
}

function transferablesFor<M extends MuPDFWorkerMethod>(
  method: M,
  params: MuPDFWorkerRequestByMethod<M>['params']
): Transferable[] {
  if (method != 'loadDocument') {
    return []
  }

  return [(params as MuPDFWorkerRequestByMethod<'loadDocument'>['params']).buffer]
}

/**
 * One `openDocument` owns one worker for exactly the caller's Scope.
 *
 * Listener callbacks only complete Effects through Deferreds or enqueue the
 * first crash for the managed reporter fiber. A crash is terminal: it fails
 * current and future requests, never restarts the worker, and termination is
 * idempotent. Interrupted requests leave the correlation map immediately;
 * late transferred bitmaps are closed instead of leaked.
 */
function makeBrowserMuPDFPlatform(createWorker: () => Worker) {
  return Effect.gen(function* () {
    const defectReporter = yield* RuntimeDefectReporter

    const openWorker = Effect.gen(function* () {
      const failureQueue = yield* Queue.unbounded<Error>()
      const failureReported = yield* Deferred.make<void>()

      yield* Queue.take(failureQueue).pipe(
        Effect.flatMap((error) => defectReporter.report('MuPDF worker crashed', error)),
        Effect.ensuring(Deferred.succeed(failureReported, undefined)),
        // renderer.open may run as an acquireRelease acquisition. The watcher
        // must not inherit that uninterruptible status or document close would
        // wait forever for its idle queue read.
        Effect.interruptible,
        Effect.forkScoped
      )

      const workerResource = yield* Effect.acquireRelease(
        Effect.try({
          try: () => {
            const worker = createWorker()
            let terminated = false

            return {
              worker,
              terminate: () => {
                if (terminated) {
                  return
                }

                terminated = true
                worker.terminate()
              }
            }
          },
          catch: (cause) => new MuPDFWorkerCrashedError(toError(cause))
        }).pipe(
          Effect.tapError((error) => defectReporter.report('MuPDF worker crashed', error.cause))
        ),
        (resource) => Effect.sync(resource.terminate)
      )

      const { worker } = workerResource
      const ready = yield* Deferred.make<void, MuPDFWorkerCrashedError>()
      const pendingByRequestId = new Map<number, PendingRequest>()
      let nextRequestId = 1
      let closed = false
      let workerFailure: MuPDFWorkerCrashedError | null = null

      const failWorker = (cause: Error) => {
        if (workerFailure) {
          return workerFailure
        }

        const failure = new MuPDFWorkerCrashedError(cause)
        workerFailure = failure
        Queue.offerUnsafe(failureQueue, cause)
        Deferred.doneUnsafe(ready, Effect.fail(failure))

        for (const pending of pendingByRequestId.values()) {
          Deferred.doneUnsafe(pending, Effect.fail(failure))
        }
        pendingByRequestId.clear()
        workerResource.terminate()
        return failure
      }

      const onMessage = (event: MessageEvent<unknown>) => {
        if (closed) {
          return
        }

        const message = event.data
        if (isMuPDFWorkerReadyMessage(message)) {
          Deferred.doneUnsafe(ready, Effect.void)
          return
        }

        if (isMuPDFWorkerCrashedMessage(message)) {
          failWorker(new Error(message.error))
          return
        }

        if (!isMuPDFWorkerResponse(message)) {
          failWorker(new Error('MuPDF worker sent an invalid protocol message'))
          return
        }

        const pending = pendingByRequestId.get(message.id)
        if (!pending) {
          if ('result' in message) {
            closeUnclaimedBitmap(message.result)
          }
          return
        }

        pendingByRequestId.delete(message.id)
        if ('error' in message) {
          Deferred.doneUnsafe(
            pending,
            Effect.fail(new MuPDFWorkerRequestError(new Error(message.error)))
          )
          return
        }

        Deferred.doneUnsafe(pending, Effect.succeed(message.result))
      }

      const onError = (event: Event) => {
        if (closed || workerFailure) {
          return
        }

        const errorEvent = event as ErrorEvent
        failWorker(toError(errorEvent.error ?? errorEvent.message ?? 'MuPDF worker error'))
      }

      const onMessageError = () => {
        if (closed || workerFailure) {
          return
        }

        failWorker(new Error('MuPDF worker message could not be deserialized'))
      }

      yield* Effect.acquireRelease(
        Effect.sync(() => {
          worker.addEventListener('message', onMessage)
          worker.addEventListener('error', onError)
          worker.addEventListener('messageerror', onMessageError)
        }),
        () =>
          Effect.gen(function* () {
            closed = true
            worker.removeEventListener('message', onMessage)
            worker.removeEventListener('error', onError)
            worker.removeEventListener('messageerror', onMessageError)
            yield* Deferred.interrupt(ready)
            yield* Effect.forEach(pendingByRequestId.values(), Deferred.interrupt, {
              discard: true
            })
            pendingByRequestId.clear()

            if (workerFailure) {
              yield* Deferred.await(failureReported)
            }
          })
      )

      const request = <M extends MuPDFWorkerMethod>(
        method: M,
        params: MuPDFWorkerRequestByMethod<M>['params']
      ): Effect.Effect<MuPDFWorkerResultByMethod<M>, MuPDFWorkerError> =>
        Effect.suspend(() => {
          if (workerFailure) {
            return Effect.fail(workerFailure)
          }
          if (closed) {
            return Effect.die(new Error('MuPDF worker session used after its Scope closed'))
          }

          const requestId = nextRequestId
          nextRequestId += 1

          return Effect.acquireUseRelease(
            Deferred.make<MuPDFWorkerResult, MuPDFWorkerError>().pipe(
              Effect.tap((pending) =>
                Effect.sync(() => {
                  pendingByRequestId.set(requestId, pending)
                })
              )
            ),
            (pending) => {
              const message = { id: requestId, method, params } as MuPDFWorkerRequest
              const send = Effect.try({
                try: () => worker.postMessage(message, transferablesFor(method, params)),
                catch: (cause) => new MuPDFWorkerSendError(toError(cause))
              }).pipe(
                Effect.catchTag('MuPDFWorkerSendError', (error) =>
                  Effect.sync(() => failWorker(error.cause)).pipe(
                    Effect.flatMap((failure) => Effect.fail(failure))
                  )
                )
              )

              return send.pipe(
                Effect.andThen(Deferred.await(pending)),
                Effect.map((result) => result as MuPDFWorkerResultByMethod<M>)
              )
            },
            () =>
              Effect.sync(() => {
                pendingByRequestId.delete(requestId)
              })
          )
        })

      yield* Deferred.await(ready)
      return { request }
    })

    return MuPDFPlatform.of({
      openDocument: (documentBytes) =>
        Effect.gen(function* () {
          const worker = yield* openWorker
          const documentStructure = yield* worker
            .request('loadDocument', { buffer: documentBytes })
            .pipe(
              Effect.catchTag('MuPDFWorkerRequestError', (error) =>
                Effect.fail(new MuPDFDocumentOpenError(error.cause))
              )
            )

          return {
            documentStructure,
            renderPageAsImage: (pageNumber, scale) =>
              worker
                .request('renderPageAsImage', { pageNumber, scale })
                .pipe(
                  Effect.catchTag('MuPDFWorkerRequestError', (error) =>
                    Effect.fail(new MuPDFPageRenderError(error.cause))
                  )
                ),
            extractPageText: (pageNumber) =>
              worker
                .request('extractPageText', { pageNumber })
                .pipe(
                  Effect.catchTag('MuPDFWorkerRequestError', (error) =>
                    Effect.fail(new MuPDFPageTextExtractionError(error.cause))
                  )
                )
          }
        })
    })
  })
}

/**
 * Builds the browser MuPDF boundary around the supplied physical worker.
 *
 * Production supplies a dedicated module worker. The existing lifecycle test
 * supplies an EventTarget-compatible worker so it can verify the boundary's
 * crash and finalization contract without replacing the Effect-native service.
 */
export function createBrowserMuPDFPlatformLayer(createWorker: () => Worker) {
  return Layer.effect(MuPDFPlatform, makeBrowserMuPDFPlatform(createWorker))
}

export const BrowserMuPDFPlatformLive = createBrowserMuPDFPlatformLayer(
  () =>
    new Worker(new URL('./workers/mupdf.worker', import.meta.url), {
      type: 'module'
    })
)
